import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import useResetScroll from "../../utils/useResetScroll";
import RenderTranslatedText from "../../components/RenderTranslatedText";
import { API_BASE_URL } from "../../config/baseUrls";

const TOPICS = [
  { value: "general", label: "General enquiry" },
  { value: "exhibitor", label: "Exhibitor enquiry" },
  { value: "sponsor", label: "Sponsor enquiry" },
  { value: "delete-account", label: "Delete my account" },
  { value: "other", label: "Something else" },
];

// Public Contact Us form: posts to the backend, which alerts our team on Slack.
const Contact = () => {
  useResetScroll();
  const [params] = useSearchParams();
  const initialTopic = TOPICS.some((t) => t.value === params.get("topic")) ? params.get("topic") : "general";
  const [topic, setTopic] = useState(initialTopic);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // hidden bot trap, must stay empty
  const [state, setState] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");

  const handleSend = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in your name, email and message.");
      return;
    }
    setError("");
    setState("sending");
    try {
      await axios.post(`${API_BASE_URL}/support/contact`, { topic, name, email, message, website, source: "web" }, { timeout: 15000 });
      setState("sent");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setState("idle");
      const status = err?.response?.status;
      if (status === 429) setError("Too many messages. Please try again in a while.");
      else if (status === 400) setError("Please check your email address and message, then try again.");
      else setError("We could not send your message. Please try again.");
    }
  };

  return (
    <div className="max-w-[450px] mx-auto p-5 flex flex-col items-center min-h-[100svh] otaPageBg">
      <p className="otaDark text-[24px] font-bold text-center my-10 mt-18">
        <RenderTranslatedText i8nKey={"Contact Us"} />
      </p>
      {state === "sent" ? (
        <div className="w-full text-center" role="status">
          <p className="text-green-700 font-medium mb-4">
            Thank you! We have your message and will reply by email.
          </p>
          <button
            onClick={() => setState("idle")}
            className="text-blue-500 text-sm rounded-xl font-medium border-2 w-full border-blue-500 p-2 bg-white"
          >
            Send another message
          </button>
        </div>
      ) : (
        <>
          <select
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="w-full p-2 border rounded-xl mb-3 bg-white"
            aria-label="Topic"
          >
            {TOPICS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Your Name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border rounded-xl mb-3"
            required
          />
          <input
            type="email"
            placeholder="Your Email"
            value={email}
            maxLength={120}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2 border rounded-xl mb-3"
            required
          />
          <textarea
            placeholder="Your Message"
            value={message}
            maxLength={2000}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full p-2 border rounded-xl mb-3 h-24"
            required
          />
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
          />
          {error && (
            <p className="text-sm text-red-600 mb-2 w-full text-center" role="alert">
              {error}
            </p>
          )}
          <button
            onClick={handleSend}
            disabled={state === "sending"}
            className="text-blue-500 text-sm text-center rounded-xl font-medium border-2 w-full border-blue-500 p-2 mb-2 bg-white mx-4 disabled:opacity-60"
          >
            {state === "sending" ? "Sending..." : <RenderTranslatedText i8nKey={"Send Message"} />}
          </button>
        </>
      )}
    </div>
  );
};

export default Contact;
